// frontend/src/components/booking-management/BookingListPanel.js

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Map } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import BookingCancelDialog from '../dialogs/BookingCancelDialog';
import BoardingPassModal from '../boarding/BoardingPassModal';
import {
  formatDate,
  formatTimeString,
  formatDateISO,
  parseLocalDate,
} from '../../utils/dateTimeFormatter';
import { calculateBookingETA, formatDurationText } from '../../utils/etaCalculations';
import bookingEtaService from '../../services/bookingEtaService';
import { isActionableRiderBooking } from '../../utils/riderTripSummary';

const getAssignedTripId = (booking) => {
  for (const candidate of [booking?.trip_id, booking?.next_occurrence_trip_id]) {
    const parsedTripId = Number(candidate);
    if (Number.isFinite(parsedTripId) && parsedTripId > 0) {
      return parsedTripId;
    }
  }
  return null;
};

const BookingListPanel = ({
  bookings,
  onSelect,
  onCancel,
  routes,
  entityId,
  referenceDataMap,
  onViewTrip,
  preferredDateFormat = 'MM/DD/YYYY',
  preferredTimeFormat = '12h',
  bookingVerificationEnabled = false,
  boardingAvailable = false
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [boardingPassModalOpen, setBoardingPassModalOpen] = useState(false);
  const [selectedBoardingBooking, setSelectedBoardingBooking] = useState(null);
  const [tripEtaDataMap, setTripEtaDataMap] = useState({});

  const tripIds = useMemo(
    () => [...new Set(bookings.map(getAssignedTripId).filter(Boolean))],
    [bookings],
  );
  const getBookingDisplayDate = (booking) =>
    booking?.booking_type === 'recurring'
      ? (booking.next_occurrence_date || booking.trip_date || booking.start_date)
      : (booking?.trip_date || booking?.start_date);
  const getRecurringProgressText = (booking) => {
    if (booking?.booking_type !== 'recurring') {
      return null;
    }

    const totalOccurrences = Number(booking.total_occurrences);
    const completedOccurrences = Number(booking.completed_occurrences);

    if (Number.isFinite(totalOccurrences) && totalOccurrences > 0) {
      const safeCompletedOccurrences = Number.isFinite(completedOccurrences) ? completedOccurrences : 0;
      const progressKey =
        safeCompletedOccurrences > 0 && safeCompletedOccurrences < totalOccurrences
          ? 'recurringPartialProgress'
          : 'recurringProgress';

      return `${t('recurringSeries')} · ${t(progressKey, {
        completed: safeCompletedOccurrences,
        total: totalOccurrences
      })}`;
    }

    return t('recurringSeries');
  };

  useEffect(() => {
    let cancelled = false;

    const loadTripEtaData = async () => {
      if (!entityId || tripIds.length === 0) {
        setTripEtaDataMap({});
        return;
      }

      try {
        const data = await bookingEtaService.getBatchTripEtaData(entityId, tripIds);
        if (!cancelled) {
          setTripEtaDataMap(data);
        }
      } catch (error) {
        console.warn('Failed to fetch batch trip-aware booking ETA data:', error);
        if (!cancelled) {
          setTripEtaDataMap({});
        }
      }
    };

    loadTripEtaData();

    return () => {
      cancelled = true;
    };
  }, [entityId, tripIds]);

  const handleCancelClick = (event, booking) => {
    event.stopPropagation();
    setSelectedBooking({
      id: booking.id,
      routeName: booking.route_name,
      date: parseLocalDate(booking.trip_date),
      time: booking.trip_time,
      passengers: `${booking.riders} ${t('riders')}, ${booking.luggages} ${t('luggage')}`,
    });
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = async () => {
    setIsProcessing(true);
    try {
      await onCancel(selectedBooking.id);
      setShowCancelDialog(false);
    } finally {
      setIsProcessing(false);
      setSelectedBooking(null);
    }
  };

  const handleViewBoardingPass = (event, booking) => {
    event.stopPropagation();

    // Format booking data for modal display
    const formattedBooking = {
      ...booking,
      formattedDate: formatDate(getBookingDisplayDate(booking), preferredDateFormat),
      formattedTime: formatTimeString(booking.trip_time, preferredTimeFormat),
      pickupLocationName: booking.pickup_name || booking.pickup_location,
      dropoffLocationName: booking.dropoff_name || booking.dropoff_location,
      tripId: getAssignedTripId(booking)
    };

    setSelectedBoardingBooking(formattedBooking);
    setBoardingPassModalOpen(true);
  };

  const handleViewTrip = (event, booking) => {
    event.stopPropagation();
    const assignedTripId = getAssignedTripId(booking);
    if (!assignedTripId) {
      return;
    }

    // Riders can land directly on their assigned trip without changing the
    // booking-list selection contract used by the surrounding view.
    onViewTrip?.(assignedTripId);
  };

  // Show all bookings based on user's filter selection (no hardcoded filtering)
  // The backend handles filtering based on status, date, time, etc.

  return (
    <div className="mb-6">
      <div className="space-y-2">
        {bookings.map((booking) => {
          const route = routes?.find((routeItem) => routeItem.id === booking.route_id);
          const assignedTripId = getAssignedTripId(booking);
          const canViewTrip = Boolean(assignedTripId) && isActionableRiderBooking(booking);
          const tripEtaData = assignedTripId ? tripEtaDataMap[assignedTripId] : null;
          const routeReferenceData = referenceDataMap?.[booking.route_id];
          const tripDate = formatDateISO(getBookingDisplayDate(booking));
          const recurringProgressText = getRecurringProgressText(booking);
          const etaInfo = route
            ? calculateBookingETA(
                tripEtaData?.waypoints || route.waypoints,
                booking.trip_time,
                tripDate,
                booking.pickup_stop_id,
                booking.dropoff_stop_id,
                routeReferenceData,
                tripEtaData?.segments || null,
              )
            : null;

          return (
            <div
              key={booking.id}
              className={cn(
                'rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer',
                background.secondary,
              )}
              onClick={() => onSelect(booking)}
            >
              <div>
                <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:justify-between sm:items-start">
                  <div className="min-w-0">
                    <div className={cn('font-medium text-base break-words', text.primary)}>{booking.route_name}</div>
                    {booking.operational_unavailability_warning?.affected && (
                      <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100">
                        {t('operationalUnavailability.existingBookingWarning')}
                      </div>
                    )}
                    {recurringProgressText && (
                      <div className={cn('text-xs mt-1', text.secondary)}>
                        {recurringProgressText}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
                    <div
                      className={cn(
                        'px-3 py-1 rounded-full text-xs font-medium flex-shrink-0',
                        booking.status === 'confirmed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : booking.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : booking.status === 'waitlist'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                          : booking.status === 'canceled'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
                      )}
                    >
                      {t(`bookingStatus.${booking.status}`)}
                    </div>
                    {/* View Boarding Pass button - Available to all users when boarding tracking enabled
                        EAV Validation: boardingAvailable validates BOTH:
                        1. Global is_active = 1 in preference_definitions
                        2. Entity-specific boarding_tracking_enabled = true
                        Note: required_role controls who can toggle the setting, not who can view passes */}
                    {booking.status === 'confirmed' &&
                     boardingAvailable && (
                      <button
                        onClick={(event) => handleViewBoardingPass(event, booking)}
                        className={cn(
                          'px-4 py-1.5 rounded-md text-sm font-medium max-w-full min-w-0 whitespace-normal text-center leading-tight',
                          'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700',
                          'transition-colors duration-200 shadow-sm hover:shadow-md',
                        )}
                      >
                        {t('viewBoardingPass')}
                      </button>
                    )}
                    {canViewTrip && (
                      <button
                        onClick={(event) => handleViewTrip(event, booking)}
                        className={cn(
                          'px-4 py-1.5 rounded-md text-sm font-medium max-w-full min-w-0 whitespace-normal text-center leading-tight inline-flex items-center gap-2',
                          'bg-slate-700 text-white hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500',
                          'transition-colors duration-200 shadow-sm hover:shadow-md',
                        )}
                      >
                        <Map className="h-4 w-4 flex-shrink-0" />
                        {t('viewTrip')}
                      </button>
                    )}
                    {(booking.status === 'pending' || booking.status === 'confirmed' || booking.status === 'waitlist') && (
                      <button
                        onClick={(event) => handleCancelClick(event, booking)}
                        className={cn(
                          'px-4 py-1.5 rounded-md text-sm font-medium max-w-full min-w-0 whitespace-normal text-center leading-tight',
                          'bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700',
                          'transition-colors duration-200 shadow-sm hover:shadow-md',
                        )}
                      >
                        {t('Cancel')}
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <div className={cn('text-sm', text.secondary)}>
                    {t('date')}:{' '}
                    {booking.booking_type === 'recurring'
                      ? booking.selected_days
                        ? booking.selected_days.join('; ')
                        : `${t('all available dates from')} ${formatDate(getBookingDisplayDate(booking), preferredDateFormat)} ${t('to')} ${formatDate(booking.end_date, preferredDateFormat)}`
                      : formatDate(getBookingDisplayDate(booking), preferredDateFormat)}
                  </div>
                  <div className={cn('text-sm', text.secondary)}>
                    {t('time')}: {formatTimeString(booking.trip_time, preferredTimeFormat)} ({t('startLocation')})
                  </div>

                  {etaInfo && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                      <div className="font-medium text-blue-900 mb-2">
                        {t('Estimated Schedule')}
                        {etaInfo.summary?.dataSource === 'gps_reference' && (
                          <span className="text-xs text-blue-600 ml-1">({t('based on historical trip')})</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="font-medium text-gray-700">
                            {t('From')}: {booking.pickup_name || etaInfo.pickupLocation.name}
                          </div>
                          <div className="text-blue-800">
                            {etaInfo.summary.isPickupStart
                              ? `${t('plannedDepartureTime')}: ${formatTimeString(
                                  etaInfo.pickupLocation.time?.toTimeString().slice(0, 5),
                                  preferredTimeFormat
                                )}`
                              : `${t('plannedArrivalTime')}: ${formatTimeString(
                                  etaInfo.pickupLocation.time?.toTimeString().slice(0, 5),
                                  preferredTimeFormat
                                )}`}
                          </div>
                          {!etaInfo.summary.isPickupStart && (
                            <div className="text-gray-500 text-xs">
                              {formatDurationText(etaInfo.summary.startToPickupMinutes)} {t('from start')}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-700">
                            {t('To')}: {booking.dropoff_name || etaInfo.dropoffLocation.name}
                          </div>
                          <div className="text-blue-800">
                            {t('plannedArrivalTime')}:{' '}
                            {formatTimeString(etaInfo.dropoffLocation.time?.toTimeString().slice(0, 5), preferredTimeFormat)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {etaInfo.summary.isPickupStart
                              ? `${formatDurationText(etaInfo.summary.totalTripMinutes)} ${t('total')}`
                              : `${formatDurationText(etaInfo.summary.pickupToDropoffMinutes)} ${t('from pickup')}, ${formatDurationText(
                                  etaInfo.summary.totalTripMinutes,
                                )} ${t('total')}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-blue-200">
                        {t('Note: Actual times may vary due to road conditions and traffic')}
                      </div>
                    </div>
                  )}

                  <div className={cn('text-sm', text.secondary)}>
                    {t('Riders')}: {booking.riders} | {t('Luggage')}: {booking.luggages}
                  </div>
                  <div className={cn('text-sm', text.secondary)}>
                    {t('contact')}: {booking.contact_number}
                  </div>
                  {bookingVerificationEnabled && booking.entity_verification_value && (
                    <div className={cn('text-sm', text.secondary)}>
                      {t('verificationInfo')}: {booking.entity_verification_value}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {bookings.length === 0 && (
          <div className={cn('text-center py-4', text.muted)}>
            {t('noBookingsFound')}
          </div>
        )}
      </div>

      <BookingCancelDialog
        isOpen={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false);
          setSelectedBooking(null);
        }}
        onConfirm={handleCancelConfirm}
        bookingDetails={selectedBooking}
        isProcessing={isProcessing}
      />

      {selectedBoardingBooking && (
        <BoardingPassModal
          open={boardingPassModalOpen}
          onClose={() => {
            setBoardingPassModalOpen(false);
            setSelectedBoardingBooking(null);
          }}
          booking={selectedBoardingBooking}
          entityId={selectedBoardingBooking.entity_id}
        />
      )}
    </div>
  );
};

export default BookingListPanel;
